import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowDown, Play } from "lucide-react";
import { MaskedLine } from "./Reveal";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1570786032462-2efc3ca8fccd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxjaHVyY2glMjB3b3JzaGlwJTIwc2lsaG91ZXR0ZSUyMGhhbmRzJTIwcmFpc2VkfGVufDB8fHx8MTc4Nzk0NTI3M3ww&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 240]);
  const opacity = useTransform(scrollY, [0, 600], [1, 0.2]);

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 -top-32">
        <img src={HERO_IMG} alt="Worship gathering" className="w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/60" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-10 pb-24 pt-40">
        <MaskedLine delay={0.2} className="mb-6">
          <span
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 text-xs md:text-sm uppercase tracking-[0.3em] text-zinc-300 border border-white/20 rounded-full px-5 py-2"
          >
            <MapPin className="w-4 h-4 text-[#4169E1]" />
            Presidential Hills &middot; Jackson, MS
          </span>
        </MaskedLine>

        <h1 className="font-display font-bold uppercase tracking-tighter leading-[0.9] text-6xl sm:text-7xl md:text-8xl lg:text-9xl">
          <MaskedLine delay={0.35}>
            <span data-testid="hero-line-1">Reaching<span className="text-[#4169E1]">.</span></span>
          </MaskedLine>
          <MaskedLine delay={0.5}>
            <span data-testid="hero-line-2">Teaching<span className="text-[#4169E1]">.</span></span>
          </MaskedLine>
          <MaskedLine delay={0.65}>
            <span data-testid="hero-line-3">Empowering<span className="text-[#4169E1]">.</span></span>
          </MaskedLine>
        </h1>

        <MaskedLine delay={0.9} className="mt-8 max-w-xl">
          <p data-testid="hero-tagline" className="text-base md:text-lg text-zinc-300 leading-relaxed">
            Hyde Park Victory Church — a place where victory becomes a way of life. Reaching the lost,
            teaching the believers, and empowering disciples to reach others.
          </p>
        </MaskedLine>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-plan-visit-button"
            onClick={() => scrollToId("contact")}
            className="inline-flex items-center gap-2 bg-[#4169E1] hover:bg-[#3555C0] text-white font-semibold uppercase tracking-widest text-sm px-8 py-4 rounded-sm transition-colors duration-300 hover:-translate-y-0.5"
          >
            Plan Your Visit
          </button>
          <a
            data-testid="hero-watch-button"
            href="https://www.youtube.com/@hydeparkvictorychurch7384"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-white/20 hover:border-[#4169E1] text-white font-semibold uppercase tracking-widest text-sm px-8 py-4 rounded-sm transition-colors duration-300 hover:-translate-y-0.5"
          >
            <Play className="w-4 h-4 text-[#4169E1]" />
            Watch Online
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-8 z-10 hidden md:block"
      >
        <ArrowDown className="w-5 h-5 text-zinc-500 animate-bounce" />
      </motion.div>
    </section>
  );
}
